import { useEffect, useMemo, useRef, useState } from 'react';

/** Lightweight searchable single-select combobox (no external deps). */
export default function Combobox({
  id,
  options,
  value,
  onChange,
  onInputChange,
  placeholder = 'Select…',
  disabled = false,
  loading = false,
  invalid = false,
  emptyMessage = 'No matches',
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const selected = options.find((o) => o.value === value) ?? null;

  // Sync the displayed text only when a dropdown option becomes selected.
  // Intentionally omits open so closing the dropdown without picking an
  // option does NOT erase manually typed text.
  useEffect(() => {
    if (selected) setQuery(selected.label);
  }, [selected]);

  useEffect(() => {
    function onDocClick(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q || (selected && q === selected.label.toLowerCase())) return options;
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        (o.sublabel?.toLowerCase().includes(q) ?? false) ||
        (o.searchText?.toLowerCase().includes(q) ?? false)
    );
  }, [options, query, selected]);

  return (
    <div ref={rootRef} className="ck-combobox">
      <input
        id={id}
        ref={inputRef}
        type="text"
        className={`ck-input ${invalid ? 'ck-input--invalid' : ''}`}
        value={query}
        placeholder={placeholder}
        disabled={disabled}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          onInputChange?.(e.target.value);
          // Always clear the dropdown selection when the user types freely
          // so the parent knows no option is confirmed.
          onChange(null);
        }}
        aria-expanded={open}
        aria-autocomplete="list"
        role="combobox"
      />
      <button
        type="button"
        tabIndex={-1}
        aria-label="Toggle options"
        className="ck-combobox-toggle"
        onClick={() => {
          if (disabled) return;
          setOpen((o) => !o);
          inputRef.current?.focus();
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && !disabled && (
        <ul role="listbox" className="ck-combobox-menu">
          {loading && <li className="ck-combobox-hint">Loadingâ€¦</li>}
          {!loading && filtered.length === 0 && (
            <li className="ck-combobox-hint">{emptyMessage}</li>
          )}
          {!loading &&
            filtered.map((o) => {
              const isSelected = o.value === value;
              return (
                <li key={o.value} role="option" aria-selected={isSelected}>
                  <button
                    type="button"
                    className={`ck-combobox-option ${isSelected ? 'is-selected' : ''}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      onChange(o.value);
                      setQuery(o.label);
                      onInputChange?.(o.label);
                      setOpen(false);
                    }}
                  >
                    <div className="ck-combobox-option-label">{o.label}</div>
                    {o.sublabel && <div className="ck-combobox-option-sub">{o.sublabel}</div>}
                  </button>
                </li>
              );
            })}
        </ul>
      )}
    </div>
  );
}



